/**
 * JWT 토큰 관리 유틸리티
 * 크로스 도메인 (.ilouli.com) 공유를 위한 쿠키 기반 관리
 * localStorage 백업으로 안정성 확보
 */

const TOKEN_KEY = 'ilouli_token';
const USER_CACHE_KEY = 'ilouli_user_cache';

/**
 * 토큰 저장
 * @param {string} token - JWT 토큰
 */
export const setToken = (token) => {
  // .ilouli.com 도메인으로 설정하여 모든 서브도메인에서 접근 가능
  const isIlouli = window.location.hostname.includes('ilouli.com');
  const domainPart = isIlouli ? '; domain=.ilouli.com' : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const maxAge = 30 * 24 * 60 * 60; // 30일

  document.cookie = `${TOKEN_KEY}=${encodeURIComponent(token)}; path=/${domainPart}; max-age=${maxAge}${secure}; SameSite=Lax`;

  // 백업으로 localStorage에도 저장 (쿠키 문제 시 복원용)
  try {
    localStorage.setItem(`${TOKEN_KEY}_backup`, token);
  } catch (e) {
    console.warn('Failed to backup token to localStorage:', e);
  }
};

/**
 * 토큰 조회
 * @returns {string|null}
 */
export const getToken = () => {
  // 먼저 쿠키에서 찾기
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === TOKEN_KEY) {
      return decodeURIComponent(value);
    }
  }

  // 쿠키에 없으면 localStorage 백업에서 복원 시도
  try {
    const backupToken = localStorage.getItem(`${TOKEN_KEY}_backup`);
    if (backupToken) {
      console.log('Restoring token from localStorage backup');
      // 쿠키로 다시 설정
      setToken(backupToken);
      return backupToken;
    }
  } catch (e) {
    console.warn('Failed to restore token from backup:', e);
  }

  return null;
};

/**
 * 토큰 삭제
 */
export const removeToken = () => {
  const isIlouli = window.location.hostname.includes('ilouli.com');
  const domainPart = isIlouli ? '; domain=.ilouli.com' : '';
  document.cookie = `${TOKEN_KEY}=; path=/${domainPart}; max-age=0`;
  // localStorage 백업도 정리
  localStorage.removeItem(`${TOKEN_KEY}_backup`);
  // 이전 버전 호환
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * 사용자 캐시 저장 (네트워크 오류 시 복원용)
 * @param {Object} user
 */
export const setUserCache = (user) => {
  try {
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
  } catch (e) {
    console.error('Failed to cache user:', e);
  }
};

/**
 * 사용자 캐시 조회
 * @returns {Object|null}
 */
export const getUserCache = () => {
  try {
    const cached = localStorage.getItem(USER_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (e) {
    console.error('Failed to get user cache:', e);
    return null;
  }
};

/**
 * 사용자 캐시 삭제
 */
export const clearUserCache = () => {
  localStorage.removeItem(USER_CACHE_KEY);
};

/**
 * 토큰 디코딩 (만료 시간 확인용)
 * @param {string} token
 * @returns {Object|null}
 */
export const decodeToken = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (e) {
    return null;
  }
};

/**
 * 토큰 만료 여부 확인
 * @param {string} token
 * @returns {boolean}
 */
export const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const now = Math.floor(Date.now() / 1000);
  return decoded.exp < now;
};
